import { useCallback, useContext, useState } from 'react'
import api from '../../api'
import { ProjectContext } from '../../contexts'
import { Project, Provider } from '../../types'
import Button from '../../ui/Button'
import Heading from '../../ui/Heading'
import { ArchiveIcon, CopyIcon, PlusIcon } from '../../ui/icons'
import { ProjectSelectModal, Table, useSearchTableState } from '../../components'
import IntegrationModal from './IntegrationModal'
import MailchimpImportModal from './MailchimpImportModal'
import DomainSetupModal from './DomainSetup'
import MessagingSetupModal from './MessagingSetup'
import { useTranslation } from 'react-i18next'
import { Menu, MenuItem } from '../../ui'
import { snakeToTitle } from '../../utils'
import Alert from '../../ui/Alert'
import Tag from '../../ui/Tag'
import Tile, { TileGrid } from '../../ui/Tile'
import toast from 'react-hot-toast/headless'

export default function Integrations() {
    const { t } = useTranslation()
    const [project] = useContext(ProjectContext)
    const state = useSearchTableState(useCallback(async params => await api.providers.search(project.id, params), [project]))
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [isImportModalOpen, setIsImportModalOpen] = useState(false)
    const [isDomainModalOpen, setIsDomainModalOpen] = useState(false)
    const [isMessagingModalOpen, setIsMessagingModalOpen] = useState(false)
    const [provider, setProvider] = useState<Provider>()
    const [domainProviderId, setDomainProviderId] = useState<number>(0)
    const [messagingProviderId, setMessagingProviderId] = useState<number>(0)
    const [providerToCopy, setProviderToCopy] = useState<Provider>()
    const handleArchive = async (id: number) => {
        if (!confirm(t('delete_integration_confirmation'))) return
        await api.providers.delete(project.id, id)
        await state.reload()
    }

    const handleCopyToProject = async (target: Project) => {
        if (!providerToCopy) return
        await api.providers.copy(project.id, providerToCopy.id, target.id)
        setProviderToCopy(undefined)
        toast.success(t('integration_copied_to_project', { project: target.name }))
    }

    // If the backend includes domain_status on a provider, this is a managed
    // account and the user should see domain setup instead of provider config
    const emailProvider = state.results?.results?.find(
        (p: Provider) => p.group === 'email' && p.domain_status !== undefined,
    )

    const showDomainAlert = emailProvider && emailProvider.domain_status !== 'verified'

    const textProvider = state.results?.results?.find(
        (p: Provider) => p.group === 'text' && p.messaging_status !== undefined,
    )

    const showMessagingAlert = textProvider && textProvider.messaging_status !== 'active'

    const openDomainSetup = (id: number) => {
        setDomainProviderId(id)
        setIsDomainModalOpen(true)
    }

    const openMessagingSetup = (id: number) => {
        setMessagingProviderId(id)
        setIsMessagingModalOpen(true)
    }

    const handleSelectRow = (p: Provider) => {
        if (p.domain_status !== undefined) {
            openDomainSetup(p.id)
            return
        }
        if (p.messaging_status !== undefined) {
            openMessagingSetup(p.id)
            return
        }
        setProvider(p)
        setIsModalOpen(true)
    }

    return (
        <>
            {showDomainAlert && (
                <Alert
                    variant={emailProvider.domain_status === 'pending' ? 'info' : 'warn'}
                    title={emailProvider.domain_status === 'pending'
                        ? t('domain_pending_alert_title')
                        : t('domain_not_configured_alert_title')
                    }
                    actions={
                        <Button
                            size="small"
                            onClick={() => openDomainSetup(emailProvider.id)}
                        >{t('domain_setup_button')}</Button>
                    }
                >
                    {emailProvider.domain_status === 'pending'
                        ? t('domain_pending_alert_body')
                        : t('domain_not_configured_alert_body')
                    }
                </Alert>
            )}
            {showMessagingAlert && (
                <Alert
                    variant={textProvider.messaging_status === 'not_configured'
                        ? 'warn'
                        : textProvider.messaging_status === 'campaign_failed'
                            ? 'error'
                            : 'info'}
                    title={t(`messaging_${textProvider.messaging_status}_alert_title`)}
                    actions={
                        <Button
                            size="small"
                            onClick={() => openMessagingSetup(textProvider.id)}
                        >{t('messaging_setup_button')}</Button>
                    }
                >
                    {t(`messaging_${textProvider.messaging_status}_alert_body`)}
                </Alert>
            )}
            <Heading size="h3" title={t('integrations')} actions={
                <Button icon={<PlusIcon />} size="small" onClick={() => {
                    setProvider(undefined)
                    setIsModalOpen(true)
                }}>{t('add_integration')}</Button>
            } />
            <Table
                {...state}
                columns={[
                    {
                        key: 'name',
                        title: t('name'),
                        cell: ({ item }) => {
                            const name = item.domain_status !== undefined
                                ? t('email_service')
                                : item.messaging_status !== undefined
                                    ? t('text_message_service')
                                    : item.name
                            return <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                {name}
                                {item.domain_status === 'not_configured' && (
                                    <Tag variant="warn" size="tiny">{t('domain_setup_required')}</Tag>
                                )}
                                {item.domain_status === 'pending' && (
                                    <Tag variant="info" size="tiny">{t('domain_pending')}</Tag>
                                )}
                                {item.messaging_status === 'not_configured' && (
                                    <Tag variant="warn" size="tiny">{t('messaging_setup_required')}</Tag>
                                )}
                                {item.messaging_status === 'campaign_failed' && (
                                    <Tag variant="error" size="tiny">{t('messaging_action_needed')}</Tag>
                                )}
                                {item.messaging_status && item.messaging_status !== 'not_configured' && item.messaging_status !== 'active' && item.messaging_status !== 'campaign_failed' && (
                                    <Tag variant="info" size="tiny">{t('messaging_in_progress')}</Tag>
                                )}
                                {item.messaging_status === 'active' && (
                                    <Tag variant="success" size="tiny">{t('messaging_active')}</Tag>
                                )}
                            </span>
                        },
                    },
                    {
                        key: 'group',
                        title: t('group'),
                        cell: ({ item: { group } }) => snakeToTitle(group),
                    },
                    { key: 'created_at', title: t('created_at') },
                    {
                        key: 'options',
                        title: t('options'),
                        align: 'end',
                        cell: ({ item }) => item.domain_status === undefined && item.messaging_status === undefined
                            ? <Menu size="small">
                                <MenuItem icon={<CopyIcon />} onClick={() => setProviderToCopy(item)}>
                                    {t('copy_to_project')}
                                </MenuItem>
                                <MenuItem icon={<ArchiveIcon />} onClick={async () => await handleArchive(item.id)}>
                                    {t('archive')}
                                </MenuItem>
                            </Menu>
                            : null,
                    },
                ]}
                itemKey={({ item }) => item.id}
                onSelectRow={handleSelectRow}
            />
            <Heading size="h4" title={t('import_title')}>
                {t('import_description')}
            </Heading>
            <TileGrid>
                <Tile
                    title={t('mailchimp')}
                    iconUrl="https://parcelvoy.com/providers/mailchimp.svg"
                    onClick={() => setIsImportModalOpen(true)}
                >
                    {t('import_mailchimp_description')}
                </Tile>
            </TileGrid>
            <MailchimpImportModal
                open={isImportModalOpen}
                onClose={setIsImportModalOpen} />
            <IntegrationModal
                open={isModalOpen}
                onClose={setIsModalOpen}
                provider={provider}
                onChange={async () => await state.reload()} />
            <DomainSetupModal
                open={isDomainModalOpen}
                onClose={setIsDomainModalOpen}
                providerId={domainProviderId} />
            <MessagingSetupModal
                open={isMessagingModalOpen}
                onClose={setIsMessagingModalOpen}
                providerId={messagingProviderId} />
            <ProjectSelectModal
                open={!!providerToCopy}
                onClose={() => setProviderToCopy(undefined)}
                onSelect={handleCopyToProject}
                title={t('copy_to_project')}
                description={t('copy_integration_to_project_description')}
                excludeIds={[project.id]} />
        </>
    )
}
